import { logger } from '@accedo/logger';
import { KeyCodes, VirtualKeys } from '@accedo/virtual-key';
import { HORIZONTAL } from '@accedo/virtual-scroll';
import { stylesheet } from 'astroturf';
import { FunctionalComponent } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { useErrorScreen } from '##/components/Error';
import { setLoadingIndicatorVisibility } from '##/components/LoadingIndicator/LoadingIndicator';
import { nav } from '##/init';
import { focusCache, VODCounter } from '##/services/store/store';
import { sendApiRequest } from '##/services/tenApi/apirequest';
import { useInterval } from '##/utils/hooks';
import KeyEventHelper from '##/utils/KeyEventHelper';
import { PageNavigation } from '##/utils/PageNavigation';
import { handleVideoClick } from '##/utils/TileClickHandlers';
import { ShowsResponse } from '##/services/tenApi/collections/types';
import { ComponentTypes } from '##/utils/types';
import { addProgressToEpisodes } from '##/utils/ContinueWatchingUtil';
import TealiumBridge from '##/services/analytics/Tealium/TealiumBridge';
import { CarouselData } from '../BaseCarousel/BaseCarousel';
import { ControlsDetails } from '../Player/PlayerTypes';
import { setNavVisibility } from '../SideNav/SideNav';
import StandardCarouselTile from '../StandardCarousel/StandardCarouselTile';
import { StandardCarouselItem } from '../StandardCarousel/types';
import PosterCarouselTile from '../StandardCarousel/PosterCarouselTile';
import { VideoUpNextLayout } from './VideoUpNextLayout';

const styles = stylesheet`
  @import '##/styles/import.scss';
    
  .container {
      width: 100vw;
      height: 100vh;

  }

`;

const VideoUpNext: FunctionalComponent<{}> = () => {
  const [videoRailData, setVideoRailData] = useState<CarouselData>();
  const [featuredData, setFeaturedData] = useState<StandardCarouselItem>();
  const [popularShowsData, setPopularShowsData] = useState<CarouselData>();
  const [previousVideoData, setPreviousVideoData] = useState<
    StandardCarouselItem
  >();
  const [pageCountdown, setPageCountdown] = useState<number>(10);
  const [countdownStopper, setCountdownStopper] = useState<boolean>(false);

  const { showError } = useErrorScreen();
  const previousVideoInformation = PageNavigation.getCurrentState() as ControlsDetails | null;
  const apiEndpoint = previousVideoInformation?.upNextContentApiEndpoint ?? '';
  const previousVideoInformationEndpoint =
    previousVideoInformation?.metadata ?? '';

  const getAllData = async (): Promise<void> => {
    let videoRailData;
    let prevInfo;
    let popularShowsDataResponse;
    if (apiEndpoint) {
      try {
        videoRailData = await sendApiRequest<StandardCarouselItem[]>(
          apiEndpoint,
        );
      } catch (err) {
        logger.error('Error fetching up next data', err);
        showError('NETWORK_ERROR');
        return;
      }

      try {
        prevInfo = await sendApiRequest(previousVideoInformationEndpoint);
      } catch (err) {
        logger.error('Error fetching previous episode data', err);
        showError('NETWORK_ERROR');
        return;
      }
    }

    if (videoRailData && prevInfo) {
      videoRailData = addProgressToEpisodes(videoRailData);
      const nextEpisode = videoRailData.shift();
      if (nextEpisode) {
        nextEpisode.functionType = 'featured-episode';
        if (nextEpisode.popularShowPostersEndpoint) {
          try {
            popularShowsDataResponse = await sendApiRequest<ShowsResponse>(
              nextEpisode.popularShowPostersEndpoint,
            );
          } catch (err) {
            logger.error('Error fetching popular shows data');
          }
        }
      }
      const scrollEndBoundary = 2;
      const carouselProps: CarouselData = {
        items: videoRailData || [],
        Tile: StandardCarouselTile,
        id: 'related-videos',
        onTileClick: (episode: StandardCarouselItem) => {
          focusCache.tvShowDetails.$set({
            epId: episode.id,
            seasonTitle: episode.season,
            isExtra: !!episode.videoTypeShortForm,
          });
          handleVideoClick(
            {
              ...episode,
              carouselTitle: 'related-videos',
              carouselVerticalIndex: 0,
              type: 'featured',
            },
            true,
          );
        },
        scrollEndBoundary,
        index: 1000,
        orientation: HORIZONTAL,
        type: ComponentTypes.Standard,
        lazyLoadBoundary: 6,
        scrollOffset: -50,
      };

      const { title: popularShowsTitle = '' } = popularShowsDataResponse || {};

      const popularShowsProps: CarouselData = {
        title: popularShowsTitle,
        items: popularShowsDataResponse?.items || [],
        Tile: PosterCarouselTile,
        id: 'popular-shows',
        onTileClick: (show) => {
          if (show.apiEndpoint) {
            focusCache.tvShowDetails.$clear();
            TealiumBridge.onCarouselItemClick({
              ...show,
              carouselTitle: popularShowsTitle,
              carouselVerticalIndex: 1,
            });

            PageNavigation.replaceRoute(`/show/${show.id}`, show.apiEndpoint);
          }
        },
        scrollEndBoundary,
        index: 1000,
        orientation: HORIZONTAL,
        type: ComponentTypes.Poster,
        lazyLoadBoundary: 6,
        scrollOffset: -50,
      };
      setPopularShowsData(popularShowsProps);
      setFeaturedData(nextEpisode);
      setVideoRailData(carouselProps);
      setPreviousVideoData(prevInfo);
    }
  };

  const onKeydown = (keyName: VirtualKeys): boolean => {
    VODCounter.$set({ episodeCount: 0 });
    if (
      keyName === KeyCodes.vKey.LEFT &&
      (nav.currentFocusNodeId === 'featured-video' ||
        nav.currentFocusNodeId === 'related-videos-item-0' ||
        nav.currentFocusNodeId === 'popular-shows-item-0')
    ) {
      return true;
    }
    if (videoRailData?.items?.length) {
      const railLength = videoRailData?.items.length;
      if (
        keyName === KeyCodes.vKey.RIGHT &&
        nav.currentFocusNodeId === `related-videos-item-${railLength - 1}`
      ) {
        return true;
      }
    }
    if (keyName === KeyCodes.vKey.DOWN || keyName === KeyCodes.vKey.RIGHT) {
      setCountdownStopper(true);
    }
    return false;
  };

  useEffect(() => {
    const previousWatchCount = VODCounter.$get().episodeCount ?? 0;
    VODCounter.$set({ episodeCount: previousWatchCount + 1 });

    setNavVisibility?.(false);
    setLoadingIndicatorVisibility?.(false);
    getAllData();
    if (
      focusCache.home
        .$get()
        .lastFocusId?.includes('continue-watching-rail-item')
    ) {
      // if we finished a continue watching video it will
      // no longer be there
      focusCache.home.$clear();
    }
    return () => {
      setNavVisibility?.(true);
    };
  }, []);

  useEffect(() => {
    if (videoRailData) {
      KeyEventHelper.addKeydownListener(onKeydown, 'up-next-page-listener');
    }
    return () => {
      KeyEventHelper.removeKeydownListener('up-next-page-listener');
    };
  }, [videoRailData]);

  useEffect(() => {
    if (featuredData) {
      nav.assignFocus('featured-area');
    }
  }, [featuredData]);

  useInterval(
    () => {
      if (featuredData) {
        if (pageCountdown > 0) {
          setPageCountdown(pageCountdown - 1);
        } else {
          focusCache.tvShowDetails.$set({
            epId: featuredData.id,
            seasonTitle: featuredData.season,
            isExtra: !!featuredData.videoTypeShortForm,
          });
          handleVideoClick(featuredData, true);
        }
      }
    },
    !countdownStopper ? 1000 : null,
  );

  return (
    <div className={styles.container}>
      {videoRailData && (
        <VideoUpNextLayout
          featuredData={featuredData}
          railData={videoRailData}
          previousData={previousVideoData}
          countDownTimer={pageCountdown}
          removeCounter={countdownStopper}
          popularShowsData={popularShowsData}
        />
      )}
    </div>
  );
};

export default VideoUpNext;
